import * as React from "react";
import { Link, Outlet } from "react-router-dom";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";
import { LiquidGlass, LiquidGlassDefs, GrainOverlay } from "@/redesign/pages/marketing/LiquidGlass";

export interface AuthLayoutProps {
  /** Top-left brand mark. */
  brand?: React.ReactNode;
  /** Optional right-side panel (testimonial / artwork / value prop). */
  side?: React.ReactNode;
  /** Disable the glass card wrapper (fall back to flat card). Default: enabled. */
  flat?: boolean;
  className?: string;
  children?: React.ReactNode;
}

/**
 * v3 liquid-glass auth shell. A single floating glass card on an
 * atmospheric backdrop — same visual language as the marketing pages.
 *
 * The glass wrap is opt-out via `flat` so legacy / specialty screens that
 * need a plain canvas can still use this layout.
 */
export function AuthLayout({
  brand,
  side,
  flat = false,
  className,
  children,
}: AuthLayoutProps) {
  return (
    <div
      className={cn(
        "relative grid min-h-dvh w-full grid-cols-1 overflow-hidden bg-[hsl(var(--rd-canvas))] text-[hsl(var(--rd-text-1))]",
        side && "lg:grid-cols-2",
        className
      )}
    >
      {/* Atmosphere — reused on the auth column only */}
      <LiquidGlassDefs />
      <GrainOverlay opacity={0.035} />
      <AuthAtmosphere />

      <div className="relative z-10 flex flex-col">
        {/* Header */}
        <header className="flex h-14 items-center px-6">
          <Link
            to="/"
            className="flex items-center gap-2 text-[14px] font-semibold tracking-tight"
          >
            {brand ?? <DefaultBrand />}
          </Link>
        </header>
        {/* Card */}
        <div className="flex flex-1 items-center justify-center px-4 pb-16">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="w-full max-w-[420px]"
          >
            {flat ? (
              <div className="w-full">{children ?? <Outlet />}</div>
            ) : (
              <LiquidGlass radius={20} elevation="mid" className="w-full p-8 sm:p-10">
                {children ?? <Outlet />}
              </LiquidGlass>
            )}
          </motion.div>
        </div>
      </div>
      {/* Side panel */}
      {side && (
        <aside className="relative z-10 hidden border-l border-[hsl(var(--rd-border))] bg-[hsl(var(--rd-surface-1))] lg:block">
          <div className="flex h-full items-center justify-center p-12">{side}</div>
        </aside>
      )}
    </div>
  );
}

/* ----------------------------------------------------------------- */
/* Auth-tuned atmosphere — gentler than the marketing one             */
/* ----------------------------------------------------------------- */
function AuthAtmosphere() {
  return (
    <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
      <motion.div
        className="absolute -top-40 -left-32 h-[520px] w-[520px] rounded-full blur-3xl"
        style={{
          background:
            "radial-gradient(circle, hsl(var(--rd-accent-500) / 0.28) 0%, transparent 60%)",
        }}
        animate={{ x: [0, 60, -20, 0], y: [0, 30, -20, 0] }}
        transition={{ duration: 24, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="absolute -bottom-32 -right-24 h-[460px] w-[460px] rounded-full blur-3xl"
        style={{
          background:
            "radial-gradient(circle, hsl(var(--rd-accent-400) / 0.22) 0%, transparent 60%)",
        }}
        animate={{ x: [0, -40, 30, 0], y: [0, -20, 20, 0] }}
        transition={{ duration: 28, repeat: Infinity, ease: "easeInOut" }}
      />
      {/* Hairline grid masked toward edges */}
      <div
        className="absolute inset-0 [background-image:linear-gradient(to_right,hsl(var(--rd-border))_1px,transparent_1px),linear-gradient(to_bottom,hsl(var(--rd-border))_1px,transparent_1px)] [background-size:48px_48px] opacity-[0.08] [mask-image:radial-gradient(ellipse_at_center,black_30%,transparent_75%)]"
      />
    </div>
  );
}

function DefaultBrand() {
  return (
    <span className="flex items-center gap-2 text-[hsl(var(--rd-text-1))]">
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-label="CreatorVault">
        <path
          d="M3 5h11l3 5-3 5H3l3-5-3-5z"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinejoin="round"
        />
      </svg>
      <span>CreatorVault</span>
    </span>
  );
}
